import * as perf from '../commons/performance-logger';
import { TimeoutError } from '../errors';
import { BaseWorker } from './BaseWorker';
import { promiseAbort, promiseTimeout } from '../utils';
import { getLogger } from '../commons/logger';
import { reporter } from '../reports/reporter';
import { timeoutMessages, stepResult, CLI_MODE } from '../commons/constants';
import { ExtensionTestPlayer } from '../player/extensionTestPlayer';
import { ChromeLauncherTestPlayer } from '../player/chromeLauncherTestPlayer';
import { gridAccessWhitelistedIp, wasInitializedFromCache } from '../commons/testimCustomToken';
import type { WebDriver } from '../player/webdriver';
import type { TestRunHandler } from '../testRunHandler';
import type { SerializedGrid } from 'src/services/gridService';

const logger = getLogger('worker-ext');

export type ExtensionPlayer = ChromeLauncherTestPlayer | ExtensionTestPlayer;

export class WorkerExtension extends BaseWorker<ExtensionPlayer> {
    override initPlayer() {
        if (this.options.useChromeLauncher) {
            return new ChromeLauncherTestPlayer(this.id);
        }
        return new ExtensionTestPlayer(this.id);
    }

    protected async _getBrowserOnce(testRunHandler: TestRunHandler, customExtensionLocalLocation: string | undefined, player: ExtensionPlayer, gridInfo: SerializedGrid) {
        const { driver } = player;
        try {
            return await driver.init({
                projectData: this.options.projectData,
                overrideConfiguration: testRunHandler.overrideTestConfig,
                browserOptions: this.options,
                testId: testRunHandler.testId,
                testName: this.testName!,
                testRunConfig: this.testRunConfig!,
                gridInfo,
                customExtensionLocalLocation,
                executionId: this.executionId,
                executionName: testRunHandler.executionName,
                testResultId: this.testResultId!,
                seleniumPerfStats: testRunHandler.seleniumPerfStats,
                fastInit: this.options.lightweightMode?.general,
                lambdatestService: this.lambdatestService,
                printFinalCaps: this.options.printFinalCaps,
                mode: CLI_MODE.EXTENSION,
                useLocalChromeDriver: this.options.useLocalChromeDriver,
                customCapabilities: testRunHandler.customCapabilities,
            });
        } catch (err) {
            logger.error('failed to get browser', {
                err,
                gridInfo,
                whitelistedPublicIp: gridAccessWhitelistedIp(),
                initializedFromCache: wasInitializedFromCache(),
                testId: testRunHandler.testId,
                resultId: testRunHandler.testResultId,
                publicIps: this.options.publicIps,
            });
            throw err;
        }
    }

    override async getBrowserOnce(testRunHandler: TestRunHandler, customExtensionLocalLocation: string | undefined, player: ExtensionPlayer, gridInfo: SerializedGrid) {
        reporter.onGetSession(this.id, this.testName, testRunHandler.runMode);
        return this._getBrowserOnce(testRunHandler, customExtensionLocalLocation, player, gridInfo);
    }

    protected isUsingChromeLauncher(player: ExtensionPlayer): player is ChromeLauncherTestPlayer {
        return Boolean(this.options.useChromeLauncher);
    }

    protected async updateTestStatusOnGrid(driver: WebDriver, success: boolean, reason?: string) {
        const status = success ? 'passed' : 'failed';
        if (this.lambdatestService.isLambdatestRun()) {
            await driver.executeJS(`lambda-status=${status}`);
        }

        if ('browserstack' in this.options && this.options.browserstack?.['browserstack.user']) {
            const executorConfig = { action: 'setSessionStatus', arguments: { status, reason } };
            await driver.execute(`browserstack_executor: ${JSON.stringify(executorConfig)}`);
        }
    }

    override async runTestOnce(testRunHandler: TestRunHandler, player: ExtensionPlayer) {
        const { testResultId, executionId, testId } = this;
        perf.log('WorkerExtension runTestOnce');

        const runViaCdpOrFallbackToAPI = async (driver: ChromeLauncherTestPlayer['driver']) => {
            const testTimeout = this.options.timeoutWasGiven ? Math.max(10000, this.options.timeout) : this.options.testStartTimeout;
            try {
                return await promiseTimeout(testRunHandler.runTestUsingCDP(driver.cdpTestRunner), testTimeout, timeoutMessages.TEST_START_TIMEOUT_MSG);
            } catch (err) {
                if (!(err instanceof TimeoutError)) {
                    throw err;
                }
                logger.warn('timeout while running test using CDP. Running checkViaRestAPIIfTestStarted', { testResultId });
                return await testRunHandler.checkViaRestAPIIfTestStarted();
            }
        };
        interface StartStatusDetails { driverUrlFinished: boolean; testRunHandlerOnStartedHadFinished: boolean }
        const navigateChromeSessionToRunTestim = async (driver: ExtensionTestPlayer['driver'], url: string, startStatusDetails: StartStatusDetails) => {
            try {
                const res = await driver.url(url); //driver.url doesn't return anything
                startStatusDetails.driverUrlFinished = true;
                return res;
            } catch (err) {
                logger.error('error from driver.url', { err, testResultId, executionId, testId, url, urlLength: url.length });
                throw err;
            }
        };
        const updateRunHandlerThatTestStartedAndChangeStatusDetails = async (startStatusDetails: StartStatusDetails) => {
            const res = await testRunHandler.onStarted(this.options.testStartTimeout);
            startStatusDetails.testRunHandlerOnStartedHadFinished = true;
            return res;
        };

        const runExtTest = async () => {
            perf.log('WorkerExtension runExtTest');
            const disableRemoteStep = this.options.lightweightMode?.disableRemoteStep || this.options.disableSockets;
            if (!disableRemoteStep) {
                testRunHandler.listenToRemoteStep(player.driver);
            }
            if (this.isUsingChromeLauncher(player)) {
                reporter.onWaitToTestStart(this.id);
                reporter.onWaitToTestComplete(this.id);
                try {
                    const testResult = await runViaCdpOrFallbackToAPI(player.driver);
                    return { ...testResult, ...testRunHandler.seleniumPerfStats.getStats() };
                } catch (err) {
                    logger.warn('failed to run test via CDP', { err });
                    throw err;
                }
            }
            const { driver } = player;
            const startStatusDetails = { driverUrlFinished: false, testRunHandlerOnStartedHadFinished: false }; //for logging / debugging purposes

            try {
                const url = await testRunHandler.getRunTestUrl();
                reporter.onWaitToTestStart(this.id);
                try {
                    await promiseTimeout(Promise.all([
                        navigateChromeSessionToRunTestim(player.driver, url, startStatusDetails),
                        updateRunHandlerThatTestStartedAndChangeStatusDetails(startStatusDetails),
                    ]), this.options.testStartTimeout, timeoutMessages.TEST_START_TIMEOUT_MSG);
                } catch (err) {
                    if (!(err instanceof TimeoutError)) {
                        throw err;
                    }
                    logger.warn('timeout occurred (see log\'s payload). Running checkViaRestAPIIfTestStarted', { testResultId, executionId, testId, ...startStatusDetails });
                    await testRunHandler.checkViaRestAPIIfTestStarted();
                }

                reporter.onWaitToTestComplete(this.id);
                const abortController = new AbortController();

                function onBrowserClosed(err: any) {
                    testRunHandler.onCompletedCleanup();
                    logger.warn('on browser closed error detected', { err, testResultId, executionId, testId });
                    driver.unregisterToClosedBrowser(onBrowserClosed);
                    driver.unregisterToKeepAliveFailed(onKeepAliveFailed);
                    err.type = stepResult.BROWSER_CLOSED;
                    abortController.abort(err);
                }
                function onKeepAliveFailed(err: any) {
                    driver.unregisterToClosedBrowser(onBrowserClosed);
                    driver.unregisterToKeepAliveFailed(onKeepAliveFailed);
                    logger.warn('on keep alive failed error detected', { err, testResultId, executionId, testId });
                    err.type = stepResult.KEEP_ALIVE_FAILED;
                    abortController.abort(err);
                }
                driver.registerToClosedBrowser(onBrowserClosed);
                driver.registerToKeepAliveFailed(onKeepAliveFailed);

                try {
                    const testRunPromise = promiseTimeout(testRunHandler.onCompleted(), this.testRunTimeout, timeoutMessages.TEST_COMPLETE_TIMEOUT_MSG);
                    const testResult = await promiseAbort(testRunPromise, abortController.signal);
                    driver.unregisterToClosedBrowser(onBrowserClosed);
                    driver.unregisterToKeepAliveFailed(onKeepAliveFailed);

                    try {
                        await this.updateTestStatusOnGrid(driver, Boolean(testResult.success), testResult.reason);
                    } catch (err) {
                        logger.error('Could not update the grid about the test status', { err });
                    }

                    if (!driver.isAlive()) {
                        logger.warn(`possible grid unresponsive for test ${this.testId}, result ${this.testResultId} (execution: ${this.executionId})`);
                        testResult.gridIssues = 'could not validate grid is alive';
                    }
                    const maxKeepAliveGap = driver.maxKeepAliveGap();
                    const MAX_KEEP_ALIVE_GAP = 30000;
                    if (maxKeepAliveGap >= MAX_KEEP_ALIVE_GAP) {
                        logger.warn(`possible browser keep alive issue ${this.testId}, result ${this.testResultId} (execution: ${this.executionId})`);
                        testResult.keepAliveIssue = maxKeepAliveGap as any as string;
                    }
                    return { ...testResult, ...testRunHandler.seleniumPerfStats.getStats() };
                } catch (err) {
                    logger.warn('timeout wait until test completed', { err, testResultId, executionId, testId });
                    // complete time out
                    throw buildError(err);
                } finally {
                    driver.unregisterToClosedBrowser(onBrowserClosed);
                    driver.unregisterToKeepAliveFailed(onKeepAliveFailed);
                }
            } catch (err) {
                logger.warn('failed to start url', { err });
                throw buildError(err);
            }
        };

        player.driver.start();

        try {
            await super.runTestOnce(testRunHandler, player);
            perf.log('WorkerExtension super.runTestOnce');
            return await runExtTest();
        } catch (err) {
            logger.error('failed to run test', {
                err,
                testId: testRunHandler.testId,
                resultId: testRunHandler.testResultId,
            });
            throw err;
        }
    }
}

function isTypedError(err: any): err is (Error & { type: string }) {
    return err instanceof Error && 'type' in err && typeof err.type === 'string';
}

function buildError(err: any) {
    return isTypedError(err) ? err : new Error(err);
}
